import React, { useState, useEffect } from "react";
import { Menu, Transition } from "@headlessui/react";
import { systemSettingKeys } from "./defaultSettings";
import { useToast } from "../common/ToastContext";
import systemSettingsService from "../../services/systemSettingsService";

const SettingsEditor = ({ item, onSave, onClose, onDelete }) => {
  const [editedSetting, setEditedSetting] = useState(
    item || {
      name: "",
      description: "",
      content: "",
      key: "",
      format: "text",
    },
  );
  const [knownSetting, setKnownSetting] = useState(false);
  const [jsonError, setJsonError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const { showToast } = useToast();

  useEffect(() => {
    if (
      editedSetting.format === "json" &&
      (editedSetting.key === systemSettingKeys.mcp?.symptomCategories ||
        editedSetting.key === systemSettingKeys.mcp?.assessmentSteps)
    ) {
      // Debounce validation
      const timer = setTimeout(() => {
        validateSymptomSettings(editedSetting.key);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [editedSetting.content, editedSetting.key, editedSetting.format]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedSetting((prev) => ({ ...prev, [name]: value }));

    if (name === "content") {
      setJsonError(null);
      setValidationErrors([]);
    }
  };

  const validateJSON = (content) => {
    try {
      const parsed = JSON.parse(content);
      setJsonError(null);
      return { valid: true, parsed };
    } catch (error) {
      const errorMsg = `Invalid JSON: ${error.message}`;
      setJsonError(errorMsg);
      return { valid: false, error: errorMsg };
    }
  };

  const validateSymptomSettings = async (settingKey) => {
    const errors = [];

    try {
      // Only validate if this is one of the MCP settings
      if (
        settingKey !== systemSettingKeys.mcp?.symptomCategories &&
        settingKey !== systemSettingKeys.mcp?.assessmentSteps
      ) {
        return;
      }

      const currentValidation = validateJSON(editedSetting.content);
      if (!currentValidation.valid) {
        return;
      }

      const currentData = currentValidation.parsed;

      let otherSetting;
      let otherKey;

      if (settingKey === systemSettingKeys.mcp?.symptomCategories) {
        otherKey = systemSettingKeys.mcp?.assessmentSteps;
      } else {
        otherKey = systemSettingKeys.mcp?.symptomCategories;
      }

      try {
        otherSetting = await systemSettingsService.getByKey(otherKey);
      } catch (error) {
        errors.push(
          `Warning: Could not find ${
            settingKey === systemSettingKeys.mcp?.symptomCategories
              ? "Assessment Steps"
              : "Symptom Categories"
          } setting for cross-validation.`,
        );
        setValidationErrors(errors);
        return;
      }

      let otherData;
      try {
        otherData = JSON.parse(otherSetting.content);
      } catch (error) {
        errors.push(
          `Warning: The ${
            settingKey === systemSettingKeys.mcp?.symptomCategories
              ? "Assessment Steps"
              : "Symptom Categories"
          } setting contains invalid JSON.`,
        );
        setValidationErrors(errors);
        return;
      }

      const currentKeys = Object.keys(currentData);

      if (settingKey === systemSettingKeys.mcp?.symptomCategories) {
        const assessmentKeys = Object.keys(otherData);
        const missingAssessments = currentKeys.filter(
          (key) => !assessmentKeys.includes(key),
        );

        if (missingAssessments.length > 0) {
          errors.push(
            `Missing assessment steps for categories: ${missingAssessments
              .slice(0, 5)
              .join(", ")}${
              missingAssessments.length > 5
                ? ` and ${missingAssessments.length - 5} more`
                : ""
            }`,
          );
        }

        const orphanedAssessments = assessmentKeys.filter(
          (key) => !currentKeys.includes(key),
        );

        if (orphanedAssessments.length > 0) {
          errors.push(
            `Assessment steps exist without matching categories: ${orphanedAssessments
              .slice(0, 5)
              .join(", ")}${
              orphanedAssessments.length > 5
                ? ` and ${orphanedAssessments.length - 5} more`
                : ""
            }`,
          );
        }
      }

      if (settingKey === systemSettingKeys.mcp?.assessmentSteps) {
        const categoryKeys = Object.keys(otherData);

        const orphanedAssessments = currentKeys.filter(
          (key) => !categoryKeys.includes(key),
        );

        if (orphanedAssessments.length > 0) {
          errors.push(
            `Assessment steps without matching categories: ${orphanedAssessments
              .slice(0, 5)
              .join(", ")}${
              orphanedAssessments.length > 5
                ? ` and ${orphanedAssessments.length - 5} more`
                : ""
            }`,
          );
        }

        // Find categories without assessments
        const missingAssessments = categoryKeys.filter(
          (key) => !currentKeys.includes(key),
        );

        if (missingAssessments.length > 0) {
          errors.push(
            `Categories exist without assessment steps: ${missingAssessments
              .slice(0, 5)
              .join(", ")}${
              missingAssessments.length > 5
                ? ` and ${missingAssessments.length - 5} more`
                : ""
            }`,
          );
        }

        Object.entries(currentData).forEach(([key, value]) => {
          if (!Array.isArray(value)) {
            errors.push(
              `Assessment for "${key}" must be an array of questions`,
            );
          } else if (value.length === 0) {
            errors.push(`Assessment for "${key}" has no questions`);
          }
        });
      }

      setValidationErrors(errors);
    } catch (error) {
      setValidationErrors([`Validation error: ${error.message}`]);
    }
  };

  const formatJSON = () => {
    try {
      const parsed = JSON.parse(editedSetting.content);
      const formatted = JSON.stringify(parsed, null, 2);
      setEditedSetting((prev) => ({ ...prev, content: formatted }));
      setJsonError(null);
      showToast("JSON formatted successfully", "info");
    } catch (error) {
      setJsonError(`Cannot format invalid JSON: ${error.message}`);
      showToast("Cannot format invalid JSON", "error");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (editedSetting.format === "json") {
      const validation = validateJSON(editedSetting.content);
      if (!validation.valid) {
        showToast("Cannot save invalid JSON", "error");
        return;
      }
    }

    if (validationErrors.length > 0) {
      const proceed = window.confirm(
        `Validation warnings found:\n\n${validationErrors.join(
          "\n",
        )}\n\nDo you want to save anyway?`,
      );
      if (!proceed) {
        return;
      }
    }

    onSave(editedSetting);
  };

  const copyBuildNumber = () => {
    fetch("/package-meta.json")
      .then((response) => response.json())
      .then((data) => {
        navigator.clipboard
          .writeText(data.v)
          .then(() => {
            showToast("Build number copied to clipboard", "info");
          })
          .catch((err) => {
            showToast("Failed to copy build number", "error");
          });
      })
      .catch((error) => showToast("Error fetching package data", "error"));
  };

  // Helper dropdown menu component
  const HelpersMenu = () => (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="bg-gray-700 border border-gray-600 text-gray-300 px-3 py-2 rounded-md hover:bg-gray-600 transition-all duration-150 flex items-center gap-2 text-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-4 h-4 text-gray-400"
          >
            <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
          </svg>
          Helpers
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-4 h-4 text-gray-400"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </Menu.Button>
      </div>
      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-gray-800 border border-gray-700 shadow-lg focus:outline-none py-1">
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={copyBuildNumber}
                className={`${
                  active ? "bg-gray-700 text-gray-100" : "text-gray-300"
                } flex w-full items-center px-4 py-2 text-sm`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-4 h-4 mr-3 text-gray-400"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.502 6h7.128A3.375 3.375 0 0118 9.375v9.375a3 3 0 003-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 00-.673-.05A3 3 0 0015 1.5h-1.5a3 3 0 00-2.663 1.618c-.225.015-.45.032-.673.05C8.662 3.295 7.554 4.542 7.502 6zM13.5 3A1.5 1.5 0 0012 4.5h4.5A1.5 1.5 0 0015 3h-1.5z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 013 20.625V9.375zm9.586 4.089a.75.75 0 00-1.172-.93l-2.25 2.83-1.242-1.24a.75.75 0 10-1.061 1.061l1.172 1.172a.75.75 0 001.078-.012l2.25-2.82a.75.75 0 00.225-.53z"
                    clipRule="evenodd"
                  />
                </svg>
                Current Build Number
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
      <div className="bg-gray-800 p-6 rounded-lg w-full max-w-7xl m-4">
        <div className="grid grid-cols-1 gap-4">
          <div className="relative">
            <input
              type="text"
              name="name"
              value={editedSetting.name}
              onChange={handleChange}
              className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
              placeholder="Setting Name"
            />
          </div>
          <div className="relative">
            <input
              type="text"
              name="key"
              value={editedSetting.key}
              onChange={handleChange}
              className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
              placeholder="Setting Key"
            />
          </div>
          <div className="relative">
            <textarea
              name="description"
              value={editedSetting.description}
              onChange={handleChange}
              className="w-full pl-8 p-2 bg-gray-700 text-white rounded resize-none dark-slim-scrollbar"
              placeholder="Description"
              rows="2"
            />
          </div>
          <div className="relative">
            {editedSetting.format === "json" && (
              <button
                type="button"
                onClick={formatJSON}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-500 z-10 flex items-center gap-1 text-sm bg-gray-700 px-2 py-1 rounded"
                title="Format JSON"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    fillRule="evenodd"
                    d="M3.5 2A1.5 1.5 0 002 3.5V5c0 .833.333 1.5.833 1.5S4 5.833 4 5V3.5h1.5a.5.5 0 000-1h-2zm9 0a.5.5 0 000 1H14v1.5c0 .833.333 1.5.833 1.5S16 5.833 16 5V3.5A1.5 1.5 0 0014.5 2h-2zM2 9.5c0-.833.333-1.5.833-1.5S4 8.667 4 9.5v1c0 .833-.333 1.5-.833 1.5S2 11.333 2 10.5v-1zm14.167-1.5c.5 0 .833.667.833 1.5v1c0 .833-.333 1.5-.833 1.5S15 11.333 15 10.5v-1c0-.833.333-1.5.833-1.5zM2 14.5c0-.833.333-1.5.833-1.5S4 13.667 4 14.5V16h1.5a.5.5 0 010 1h-2A1.5 1.5 0 012 15.5v-1zm14.167-1.5c.5 0 .833.667.833 1.5v1a1.5 1.5 0 01-1.5 1.5h-2a.5.5 0 010-1H14v-1.5c0-.833.333-1.5.833-1.5z"
                    clipRule="evenodd"
                  />
                </svg>
                Format
              </button>
            )}
            {editedSetting.format === "dropdown" ? (
              <select
                name="content"
                value={editedSetting.content}
                onChange={handleChange}
                className="w-full p-2 bg-gray-700 text-white rounded"
              >
                {item?.values?.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            ) : (
              <textarea
                name="content"
                value={editedSetting.content}
                onChange={handleChange}
                className="w-full p-2 bg-[#1F1F1F] font-mono text-white rounded dark-slim-scrollbar h-[400px] resize-none"
                placeholder={
                  editedSetting.format === "json"
                    ? "Enter valid JSON..."
                    : "Setting Content"
                }
                rows="20"
              />
            )}
            {jsonError && (
              <div className="mt-2 p-2 bg-red-900 border border-red-700 rounded text-red-200 text-sm">
                {jsonError}
              </div>
            )}
            {validationErrors.length > 0 && (
              <div className="mt-2 p-2 bg-yellow-900 border border-yellow-700 rounded text-yellow-200 text-sm">
                <div className="font-semibold mb-1">
                  ⚠️ Validation Warnings:
                </div>
                <ul className="list-disc list-inside">
                  {validationErrors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="mt-8 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={onDelete}
              className={`${
                knownSetting ? "bg-orange-600" : "bg-red-600"
              } text-white px-4 py-2 rounded ${
                knownSetting ? "hover:bg-orange-700" : "hover:bg-red-700"
              }`}
            >
              {knownSetting ? "Revert" : "Delete"}
            </button>
            {/* Add Helpers dropdown menu */}
            <HelpersMenu />
          </div>
          <select
            name="format"
            value={editedSetting.format.toLowerCase()}
            onChange={handleChange}
            className={`py-1 px-3 rounded-sm text-xs font-medium border-0 cursor-pointer ${
              editedSetting.format === "markdown"
                ? "bg-green-600 text-white"
                : editedSetting.format === "json"
                  ? "bg-blue-600 text-white"
                  : editedSetting.format === "yaml"
                    ? "bg-purple-600 text-white"
                    : editedSetting.format === "dropdown"
                      ? "bg-teal-600 text-white"
                      : "bg-gray-600 text-white"
            }`}
          >
            <option value="text">TEXT</option>
            <option value="markdown">MARKDOWN</option>
            <option value="json">JSON</option>
            <option value="yaml">YAML</option>
            <option value="dropdown">DROPDOWN</option>
          </select>
          <div className="flex items-center space-x-4">
            <div>
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-600 text-white px-4 py-2 rounded mr-2 hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
                onClick={handleSubmit}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsEditor;
